import {
  OktonInput,
  OktonSelect,
  OktonAlert,
  OktonSearchSelect,
  OktonPageHeader,
} from '@okton/platform-ui';
import {
  Box,
  Button,
  Flex,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Check } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import BankAppPage from '../../../layout/BankAppPage';
import {
  AGENCIA_NOVA,
  BANCO_COM_POSTO,
  identificacaoAgencia,
  MOEDA_NOVA,
  useAgencias,
  useBancos,
  useContasBancarias,
  useMoedasDisponiveis,
  useTiposConta,
} from '../../../services/hooks/useContasBancarias';
import {
  useCreateContaBancaria,
  useUpdateContaBancaria,
} from '../../../services/hooks/useContasBancariasMutations';
import { limparEntradaValor, valorParaNumero } from '../../../utils/valorInput';

const VAZIO = {
  descricao: '',
  banco: '',
  agencia: '',
  agencianumero: '',
  agenciadigito: '',
  agenciaposto: '',
  numero: '',
  digito: '',
  tipo: '1',
  moeda: '',
  saldoinicial: '',
};

/**
 * Cadastro e edição de Conta Bancária em rota própria, não em pop-up.
 *
 * A mesma tela atende /contas-bancarias/nova e /contas-bancarias/:idConta — o
 * que muda é ter ou não uma conta carregada da listagem.
 */
const ContaBancariaForm = () => {
  const history = useHistory();
  const { idConta } = useParams<{ idConta?: string }>();
  const bg = useColorModeValue('white', 'gray.800');
  const color = useColorModeValue('#3f3f46', '#d4d4d8');

  const { data: accounts = [] } = useContasBancarias();
  const editing = accounts.find(a => a.id_cts === idConta) ?? null;

  const { data: bancos = [] } = useBancos();
  const { data: agencias = [] } = useAgencias();
  const { data: tiposConta = [] } = useTiposConta();
  const createMutation = useCreateContaBancaria();
  const updateMutation = useUpdateContaBancaria();

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...VAZIO });
  const [error, setError] = useState('');

  // Só Moeda do Banco escolhido: no cadastro a Conta ainda não existe, então
  // o filtro vai pelo Banco da tela.
  const { data: moedas = [] } = useMoedasDisponiveis(
    editing?.id_cts,
    form.banco,
  );

  // A listagem é carregada por hook, então a conta em edição pode chegar
  // depois do primeiro render: preenche o formulário quando ela aparecer.
  useEffect(() => {
    if (!editing) return;
    setForm({
      descricao: editing.descricao_cts,
      banco: editing.id_ban ?? '',
      agencia: editing.agencia_cts ?? '',
      agencianumero: '',
      agenciadigito: '',
      agenciaposto: '',
      numero: editing.numero_cts ?? '',
      digito: editing.digito_cts ?? '',
      tipo: editing.tipo_cts || '1',
      moeda: editing.moeda_ctm ?? '',
      saldoinicial: String(editing.saldoinicial_cts ?? '').replace('.', ','),
    });
  }, [editing]);

  // O IFLEX troca Dígito por Posto quando o banco é o Sicredi (código de
  // compensação 748), então a tela pede o mesmo campo que ele pede.
  const bancoUsaPosto =
    bancos.find(b => b.id_ban === form.banco)?.confcomp_ban === BANCO_COM_POSTO;

  const save = async () => {
    if (!form.descricao.trim() || !form.numero.trim()) {
      setError('Descrição e número da conta são obrigatórios.');
      return;
    }
    // Sem Moeda o Monitor Bancário não resolve a Conta a partir do lançamento,
    // e a conciliação não encontra título nenhum.
    if (!form.moeda) {
      setError('Selecione a Moeda vinculada a esta conta.');
      return;
    }
    if (form.moeda === MOEDA_NOVA && !form.banco) {
      setError('Selecione o Banco: a nova Moeda recebe o nome dele.');
      return;
    }
    if (form.agencia === AGENCIA_NOVA && !form.agencianumero.trim()) {
      setError('Informe o número da Agência.');
      return;
    }
    if (form.agencia === AGENCIA_NOVA && !form.banco) {
      setError('Selecione o Banco: a Agência é cadastrada nele.');
      return;
    }
    setSaving(true);
    setError('');

    const payload = {
      descricao: form.descricao.trim(),
      banco: form.banco,
      agencia: form.agencia,
      agencianumero: form.agencianumero.trim(),
      agenciadigito: form.agenciadigito.trim(),
      agenciaposto: form.agenciaposto.trim(),
      numero: form.numero.trim(),
      digito: form.digito.trim(),
      tipo: form.tipo,
      moeda: form.moeda,
      saldoinicial: String(valorParaNumero(form.saldoinicial) || 0),
    };

    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id_cts, ...payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      history.push('/contas-bancarias');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Erro ao salvar conta bancária.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="CADASTROS"
        title={editing ? 'Editar Conta Bancária' : 'Nova Conta Bancária'}
        subtitle="Dados da conta usada na conciliação"
        onBack={() => history.push('/contas-bancarias')}
      />

      <Box bg={bg} mt="4" borderWidth="1px" rounded="2xl" p="6">
        <Box maxW="820px">
          <Stack spacing="4">
            {error && <OktonAlert status="error" description={error} />}

            <Box>
              <Text
                as="label"
                htmlFor="conta-bancaria-descricao"
                display="block"
                fontSize="sm"
                fontWeight="medium"
                mb="1"
              >
                Descrição *
              </Text>
              <OktonInput
                name="conta-bancaria-descricao"
                color={color}
                placeholder="Ex: Conta Corrente PJ"
                maxLength={60}
                value={form.descricao}
                onChange={e => {
                  const valor = e.target.value;
                  setForm(f => ({ ...f, descricao: valor }));
                }}
              />
            </Box>

            <Box>
              <Text
                as="label"
                htmlFor="banco"
                display="block"
                fontSize="sm"
                fontWeight="medium"
                mb="1"
              >
                Banco
              </Text>
              <OktonSearchSelect
                name="banco"
                placeholder="Selecione"
                value={form.banco}
                onChange={valor =>
                  setForm(f => ({
                    ...f,
                    banco: valor,
                    // A Moeda escolhida era do Banco anterior. A nova segue o
                    // nome do Banco, então continua válida.
                    moeda: f.moeda === MOEDA_NOVA ? f.moeda : '',
                  }))
                }
                options={[
                  ...bancos.map(b => ({
                    value: b.id_ban,
                    label: b.nome_ban,
                  })),
                ]}
              />
            </Box>

            <Box>
              <Text
                as="label"
                htmlFor="moeda"
                display="block"
                fontSize="sm"
                fontWeight="medium"
                mb="1"
              >
                Moeda *
              </Text>
              <OktonSearchSelect
                name="moeda"
                placeholder="Selecione"
                value={form.moeda}
                onChange={valor => setForm(f => ({ ...f, moeda: valor }))}
                options={[
                  { value: MOEDA_NOVA, label: 'Nova Moeda' },
                  ...moedas.map(m => ({
                    value: m.id_moe,
                    label: m.nome_moe,
                  })),
                ]}
              />
              <Text fontSize="xs" color="gray.500" mt="1">
                {!form.banco
                  ? 'Selecione o Banco: só Moeda dele pode ser usada aqui.'
                  : form.moeda === MOEDA_NOVA
                  ? 'A Moeda será criada com o nome do Banco selecionado.'
                  : 'Lançamentos feitos com essa Moeda são vinculados a esta conta.'}
              </Text>
            </Box>

            <Box>
              <Text
                as="label"
                htmlFor="agencia"
                display="block"
                fontSize="sm"
                fontWeight="medium"
                mb="1"
              >
                Agência
              </Text>
              <OktonSearchSelect
                name="agencia"
                placeholder="Selecione"
                value={form.agencia}
                onChange={valor => setForm(f => ({ ...f, agencia: valor }))}
                options={[
                  { value: AGENCIA_NOVA, label: 'Nova Agência' },
                  ...agencias.map(a => ({
                    value: a.id_agc,
                    label: identificacaoAgencia(
                      a.agencia_agc,
                      a.digito_agc,
                      a.posto_agc,
                    ),
                  })),
                ]}
              />
            </Box>

            {/* O número só é pedido ao cadastrar: para agência existente ele
          já está no IFLEX. */}
            {form.agencia === AGENCIA_NOVA && (
              <Box>
                <SimpleGrid columns={3} spacing="4">
                  <Box gridColumn="span 2">
                    <Text
                      as="label"
                      htmlFor="conta-bancaria-agencianumero"
                      display="block"
                      fontSize="sm"
                      fontWeight="medium"
                      mb="1"
                    >
                      Número da Agência *
                    </Text>
                    <OktonInput
                      name="conta-bancaria-agencianumero"
                      color={color}
                      maxLength={10}
                      placeholder="0000"
                      value={form.agencianumero}
                      onChange={e => {
                        const valor = e.target.value;
                        setForm(f => ({ ...f, agencianumero: valor }));
                      }}
                    />
                  </Box>
                  {/* O IFLEX alterna entre os dois: para o Sicredi a agência
                    tem Posto, para os demais tem Dígito. Mostrar os dois
                    aqui faria o usuário preencher um campo que some lá. */}
                  {bancoUsaPosto ? (
                    <Box>
                      <Text
                        as="label"
                        htmlFor="conta-bancaria-agenciaposto"
                        display="block"
                        fontSize="sm"
                        fontWeight="medium"
                        mb="1"
                      >
                        Posto
                      </Text>
                      <OktonInput
                        name="conta-bancaria-agenciaposto"
                        color={color}
                        maxLength={2}
                        placeholder="00"
                        value={form.agenciaposto}
                        onChange={e => {
                          const valor = e.target.value;
                          setForm(f => ({ ...f, agenciaposto: valor }));
                        }}
                      />
                    </Box>
                  ) : (
                    <Box>
                      <Text
                        as="label"
                        htmlFor="conta-bancaria-agenciadigito"
                        display="block"
                        fontSize="sm"
                        fontWeight="medium"
                        mb="1"
                      >
                        Dígito
                      </Text>
                      <OktonInput
                        name="conta-bancaria-agenciadigito"
                        color={color}
                        maxLength={1}
                        placeholder="0"
                        value={form.agenciadigito}
                        onChange={e => {
                          const valor = e.target.value;
                          setForm(f => ({ ...f, agenciadigito: valor }));
                        }}
                      />
                    </Box>
                  )}
                </SimpleGrid>
                <Text fontSize="xs" color="gray.500" mt="1">
                  A Agência é cadastrada no Banco selecionado, com o número como
                  nome.
                </Text>
              </Box>
            )}

            <SimpleGrid columns={3} spacing="4">
              <Box gridColumn="span 2">
                <Text
                  as="label"
                  htmlFor="conta-bancaria-numero"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Conta *
                </Text>
                <OktonInput
                  name="conta-bancaria-numero"
                  color={color}
                  placeholder="00000"
                  maxLength={10}
                  value={form.numero}
                  onChange={e => {
                    const valor = e.target.value;
                    setForm(f => ({ ...f, numero: valor }));
                  }}
                />
              </Box>
              <Box>
                <Text
                  as="label"
                  htmlFor="conta-bancaria-digito"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Dígito
                </Text>
                <OktonInput
                  name="conta-bancaria-digito"
                  color={color}
                  maxLength={1}
                  placeholder="0"
                  value={form.digito}
                  onChange={e => {
                    const valor = e.target.value;
                    setForm(f => ({ ...f, digito: valor }));
                  }}
                />
              </Box>
            </SimpleGrid>

            <SimpleGrid columns={2} spacing="4">
              <Box>
                <Text
                  as="label"
                  htmlFor="tipo"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Tipo
                </Text>
                <OktonSelect
                  name="tipo"
                  color={color}
                  value={form.tipo}
                  onChange={e => {
                    const valor = e.target.value;
                    setForm(f => ({ ...f, tipo: valor }));
                  }}
                >
                  {tiposConta.map(t => (
                    <option key={t.valor} value={String(t.valor)}>
                      {t.descricao}
                    </option>
                  ))}
                </OktonSelect>
              </Box>
              <Box>
                <Text
                  as="label"
                  htmlFor="conta-bancaria-saldoinicial"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Saldo Inicial (R$)
                </Text>
                <OktonInput
                  name="conta-bancaria-saldoinicial"
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  color={color}
                  value={form.saldoinicial}
                  onChange={e => {
                    const valor = e.target.value;
                    setForm(f => ({
                      ...f,
                      saldoinicial: limparEntradaValor(valor, true),
                    }));
                  }}
                />
              </Box>
            </SimpleGrid>
          </Stack>

          <Flex gridGap="3" pt="6">
            <Button
              variant="ghost"
              onClick={() => history.push('/contas-bancarias')}
            >
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              leftIcon={saving ? <Spinner size="sm" /> : <Check size={16} />}
              isDisabled={saving}
              onClick={save}
            >
              {editing ? 'Salvar' : 'Cadastrar'}
            </Button>
          </Flex>
        </Box>
      </Box>
    </BankAppPage>
  );
};

export default ContaBancariaForm;
